<script lang="ts">
  import { page } from "$app/stores";
  import type { Contributions, ProcessVideo } from "$types";
  import { get_pattern } from "$lib/utilities";
  import { mobile_size_large } from "$components/episodes/gallery_store";
  import { lazyLoad } from "$lib/actions/lazyLoad";
  import { browser } from "$app/environment";
  import { modalActive } from "$lib/data";
  import { fade } from "svelte/transition";
  import Spinner from "$components/forms/Spinner.svelte";

  export let context: string = "gallery";
  export let base: string;

  export let contribution: Contributions;
  const thumbnail_url = contribution.thumbnail
    ? `https://backend.craftroulette.live/assets/${contribution.thumbnail}?key=thumbnail`
    : `https://backend.craftroulette.live/assets/${contribution.image}?key=thumbnail`;

  // we need to check if there is a matching process video
  let process_video: ProcessVideo;
  if ($page.data?.episode?.process_videos && contribution.member) {
    const video_match = $page.data?.episode?.process_videos.find(
      (video: ProcessVideo) => video.member?.id === contribution.member,
    );
    if (video_match && video_match.member?.support_level >= 1) {
      process_video = video_match;
    }
  }

  // we now have feedback when the modal code is loading
  let loading: boolean = false;
  $: if ($modalActive && browser) loading = false;
</script>

<div class="contribution" class:mobile_small={!$mobile_size_large}>
  {#if context === "live"}
    {#if contribution.host}
      <div class="ribbon hidesmall" class:host={contribution.host}>
        <span>
          {#if contribution.host}
            Host
          {:else}
            Guest
          {/if}
        </span>
      </div>
    {/if}
  {/if}
  <a
    class="thumb"
    href="{base}/{contribution.image}"
    tabindex="0"
    data-sveltekit-noscroll
    data-sveltekit-preload-code
    on:click={() => (loading = true)}
  >
    <img
      alt="Contribution by {contribution.name}"
      src="/lazy.gif"
      use:lazyLoad={thumbnail_url}
      class="lazy"
    />
    {#if loading}
      <div class="loading" transition:fade={{ duration: 100 }}>
        <Spinner />
      </div>
    {/if}
  </a>
  <div
    class="meta"
    class:lesspadding={contribution.member || contribution.extra_image_1}
  >
    {#if contribution.episode?.episode_number}
      <div class="name">
        Episode #{contribution.episode?.episode_number}
      </div>
    {/if}
    {#if contribution.how_many_number}
      <div class="how_many">
        #{contribution.how_many_number}
      </div>
    {/if}
    {#if contribution.how_many}
      <div class="how_many">
        {contribution.how_many}
      </div>
    {/if}
  </div>
  {#if contribution.member || contribution.extra_image_1}
    <div class="tags">
      {#if contribution.extra_image_1}
        <div>Extra Images</div>
      {/if}
      {#if process_video}
        <div>Process Video</div>
      {/if}
    </div>
  {/if}
  <div class:nonmem={!contribution.member} class="buttons">
    <a
      href="{base}/{contribution.image}"
      tabindex="0"
      data-sveltekit-noscroll
      data-sveltekit-preload-code
      on:click={() => (loading = true)}
      style:background-image={get_pattern()}
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" center
        ><rect width="256" height="256" fill="none" /><polygon
          points="32 120 128 24 224 120 176 120 176 184 80 184 80 120 32 120"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="16"
        /><line
          x1="176"
          y1="216"
          x2="80"
          y2="216"
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="16"
        /></svg
      >
      <span class="hidesmall"> Details </span>
    </a>

    {#if contribution.member}
      <a
        href="{base}/{contribution.image}?comments=true"
        tabindex="0"
        data-sveltekit-noscroll
        data-sveltekit-preload-code
        on:click={() => (loading = true)}
        style:background-image={get_pattern()}
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"
          ><rect width="256" height="256" fill="none" /><line
            x1="96"
            y1="104"
            x2="160"
            y2="104"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="16"
          /><line
            x1="96"
            y1="136"
            x2="160"
            y2="136"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="16"
          /><path
            d="M105.07,192l16,28a8,8,0,0,0,13.9,0l16-28H216a8,8,0,0,0,8-8V56a8,8,0,0,0-8-8H40a8,8,0,0,0-8,8V184a8,8,0,0,0,8,8Z"
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="16"
          /></svg
        >
        <span class="hidesmall"> Discussion </span>
        {#if contribution.discussion_count && contribution.discussion_count > 0}
          <span class="count">{contribution.discussion_count}</span>
        {/if}
      </a>
    {/if}
  </div>
</div>

<style>
  .count {
    background: white;
    color: var(--offDark);
    padding: 0.2rem 0.3rem 0.1rem 0.3rem;
    border-radius: 0.25rem;
    font-size: 0.6rem;
    font-weight: bold;
    border: 1px solid var(--dots);
    position: relative;
    top: -1.5px;
  }

  .contribution {
    border: none;
    padding: 0;
    text-decoration: none;
    background: transparent;
    background-color: white;
    box-shadow: var(--shadow);
    border-radius: 1rem;
    display: flex;
    flex-flow: column;
    position: relative;
  }
  .contribution .ribbon {
    position: absolute;
    right: -5px;
    top: -5px;
    z-index: 1;
    overflow: hidden;
    width: 75px;
    height: 75px;
    text-align: right;
    z-index: 2;
  }
  .contribution .ribbon > span {
    font-size: 10px;
    font-weight: bold;
    color: #fff;
    text-transform: uppercase;
    text-align: center;
    line-height: 20px;
    transform: rotate(45deg);
    -webkit-transform: rotate(45deg);
    width: 100px;
    display: block;
    background: #58ccaf;
    background: linear-gradient(#617980 0%, #6f8f99 100%);
    box-shadow: 0 3px 10px -5px black;
    position: absolute;
    top: 19px;
    right: -21px;
  }
  .contribution .ribbon > span::before {
    content: "";
    position: absolute;
    left: 0px;
    top: 100%;
    z-index: -1;
    border-left: 3px solid #414f53;
    border-right: 3px solid transparent;
    border-bottom: 3px solid transparent;
    border-top: 3px solid #414f53;
  }
  .contribution .ribbon > span::after {
    content: "";
    position: absolute;
    right: 0px;
    top: 100%;
    z-index: -1;
    border-left: 3px solid transparent;
    border-right: 3px solid #414f53;
    border-bottom: 3px solid transparent;
    border-top: 3px solid #414f53;
  }
  .contribution .tags {
    display: flex;
    padding: 0.3rem 1rem 0.6rem 1rem;
    gap: 0.5rem;
  }
  .contribution .tags > div {
    font-size: 0.6rem;
    padding: 0.2rem 0.4rem 0.15rem 0.4rem;
    outline: 1px solid var(--dots);
    color: var(--offDark);
    border-radius: 0.25rem;
    text-transform: uppercase;
    font-weight: bold;
    display: flex;
  }
  .contribution .meta {
    padding: 1rem;
    width: 100%;
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    overflow: hidden;
    flex-wrap: wrap;
  }
  .contribution .meta.lesspadding {
    padding: 1rem 1rem 0.2rem 1rem;
  }
  .contribution .meta .name {
    font-weight: bold;
    color: var(--title);
    font-size: 1rem;
  }
  .contribution .meta .how_many {
    font-weight: normal;
    color: var(--offDark);
    font-size: 0.8rem;
    font-weight: bold;
  }
  .contribution .buttons {
    border-top: 1px solid var(--dots);
    display: grid;
    grid-template-columns: 1fr 1fr;
    border-radius: 0 0 1rem 1rem;
    overflow: hidden;
  }
  .contribution .buttons.nonmem {
    grid-template-columns: 1fr;
  }
  .contribution .buttons a {
    background: white;
    border: none;
    display: flex;
    flex: 1;
    color: var(--offDark);
    padding: 0.75rem;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    font-weight: bold;
  }
  .contribution .buttons a:first-of-type {
    border-right: 1px solid var(--dots);
  }
  .contribution .buttons a svg {
    width: 1rem;
    height: 1rem;
    color: var(--secondary);
    position: relative;
    top: -1px;
  }
  .contribution a.thumb {
    width: 100%;
    transition: all 150ms ease;
    background: #eeeeee;
    background: var(--light);
    overflow: hidden;
    position: relative;
    padding-bottom: 100%;
    margin: 0;
    border: 0;
    padding: 0;
    display: flex;
    aspect-ratio: 1 / 1;
    border-bottom: 1px solid var(--dots);
    border-radius: 1rem 1rem 0 0;
  }
  .contribution a.thumb img {
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    -ms-transform: translateZ(0);
    -webkit-transform: translateZ(0);
    transform: translateZ(0);
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
  }
  .contribution a.thumb .loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: grid;
    place-content: center;
    z-index: 3;
    background: rgba(31, 39, 45, 0.8);
  }
  .contribution a.thumb:hover img {
    transform: scale(110%);
    transition: all 150ms ease-in-out;
  }

  @media (max-width: 550px) {
    .mobile_small .hidesmall {
      display: none;
    }
    .mobile_small .contribution .meta .name {
      font-size: 0.8rem;
    }
    .mobile_small .contribution .meta .how_many {
      color: var(--offDark);
      font-size: 0.7rem;
    }
  }
</style>
